import { useState, type CSSProperties } from "react";
import { PageHeader } from "../components/common";
import { LoadingState } from "../components/dashboard/LoadingState";
import { QuickActionButton } from "../components/dashboard/QuickActionButton";
import { ComposeMessageModal } from "../components/messages/ComposeMessageModal";
import { useAuth } from "../hooks/useAuth";
import { useMessages } from "../hooks/useMessages";
import type { Message } from "../services/message.service";

const FILTER_OPTIONS: {
  label: string;
  value: "all" | "unread" | "important" | "sent";
}[] = [
  { label: "All", value: "all" },
  { label: "Unread", value: "unread" },
  { label: "Important", value: "important" },
  { label: "Sent", value: "sent" },
];

const WEB_COLORS = {
  primary: "#3b82f6",
  primaryText: "#111827",
  secondaryText: "#6b7280",
  bodyText: "#374151",
  border: "#d1d5db",
  surface: "#ffffff",
  surfaceMuted: "#f9fafb",
  unreadSurface: "#f8fafc",
  avatarBg: "#e0f2fe",
  accent: "#2563eb",
  danger: "#b91c1c",
  dangerLight: "#fff1f2",
  importantLight: "#fee2e2",
  unreadBorder: "#3b82f6",
  statShadow: "0px 10px 15px rgba(15, 23, 42, 0.05)",
  priority: {
    high: "#ef4444",
    normal: "#3b82f6",
    low: "#9ca3af",
  },
};

const CARD_STYLE: CSSProperties = {
  border: `1px solid ${WEB_COLORS.border}`,
  borderRadius: "12px",
  padding: "20px",
  display: "flex",
  gap: "16px",
  backgroundColor: WEB_COLORS.surface,
  boxShadow: WEB_COLORS.statShadow,
};

const ACTION_BUTTON_STYLE: CSSProperties = {
  borderRadius: "8px",
  padding: "8px 16px",
  width: "160px",
  minHeight: "36px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  fontSize: "14px",
};

export function Messages() {
  const { currentUser } = useAuth();
  const {
    messages,
    sentMessages,
    filteredMessages,
    loading,
    unreadCount,
    activeFilter,
    setActiveFilter,
    markAsRead,
    markAllAsRead,
    toggleImportant,
    deleteMessage,
  } = useMessages();

  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const isSentView = activeFilter === "sent";

  const handleMessageClick = async (message: Message) => {
    if (isSentView) {
      return;
    }
    if (!message.isRead) {
      await markAsRead(message.id as string);
    }
  };

  const handleReply = (message: Message) => {
    setReplyTo(message);
    setIsComposeOpen(true);
  };

  const handleComposeNew = () => {
    setReplyTo(null);
    setIsComposeOpen(true);
  };

  const tz = currentUser?.companyTimezone;
  const formatTimestamp = (timestamp?: { toDate?: () => Date }) => {
    if (!timestamp?.toDate) return "";
    const date = timestamp.toDate();
    return date.toLocaleString("en-IE", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...(tz && { timeZone: tz }),
    });
  };

  const stats = [
    { label: "Unread", value: unreadCount },
    {
      label: "Important",
      value: messages.filter((msg) => msg.isImportant).length,
    },
    { label: "Received", value: messages.length },
    { label: "Sent", value: sentMessages.length },
  ];

  if (!currentUser) {
    return <LoadingState message="Loading inbox..." />;
  }

  return (
    <div
      style={{
        padding: "32px",
        minHeight: "100%",
        backgroundColor: WEB_COLORS.surfaceMuted,
      }}
    >
      <PageHeader
        title="Inbox"
        subtitle="Keep up with staffing updates, schedule changes, and admin announcements."
        actions={
          <QuickActionButton
            label="Compose Message"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            }
            variant="secondary"
            onClick={handleComposeNew}
          />
        }
      />

      <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
        {stats.map((stat) => (
          <div
            key={stat.label}
            style={{
              flex: 1,
              backgroundColor: WEB_COLORS.surfaceMuted,
              borderRadius: "10px",
              padding: "16px",
            }}
          >
            <div style={{ fontSize: "13px", color: WEB_COLORS.secondaryText }}>
              {stat.label}
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 700,
                color: WEB_COLORS.primaryText,
              }}
            >
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", gap: "8px" }}>
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setActiveFilter(option.value)}
              style={{
                borderRadius: "999px",
                border: `1px solid ${WEB_COLORS.border}`,
                padding: "8px 16px",
                backgroundColor:
                  activeFilter === option.value
                    ? WEB_COLORS.primaryText
                    : WEB_COLORS.surface,
                color:
                  activeFilter === option.value
                    ? WEB_COLORS.surface
                    : WEB_COLORS.bodyText,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {!isSentView && unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            style={{
              border: "none",
              background: "transparent",
              color: WEB_COLORS.primary,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <LoadingState message="Loading messages..." />
      ) : filteredMessages.length === 0 ? (
        <div
          style={{
            border: `1px dashed ${WEB_COLORS.border}`,
            borderRadius: "12px",
            padding: "48px",
            textAlign: "center",
            color: WEB_COLORS.secondaryText,
          }}
        >
          No messages found for this filter.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {filteredMessages.map((message) => {
            const participant = isSentView
              ? message.toUser?.name || message.to
              : message.fromUser?.name || message.fromName;
            return (
              <div
                key={message.id as string}
                style={{
                  ...CARD_STYLE,
                  borderLeft:
                    !isSentView && !message.isRead
                      ? `4px solid ${WEB_COLORS.unreadBorder}`
                      : "4px solid transparent",
                  backgroundColor:
                    !isSentView && !message.isRead
                      ? WEB_COLORS.unreadSurface
                      : WEB_COLORS.surface,
                }}
                onClick={() => handleMessageClick(message)}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: WEB_COLORS.avatarBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  {(participant || "S")[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: message.isRead ? 600 : 700,
                        color: WEB_COLORS.primaryText,
                      }}
                    >
                      {message.subject}
                      {message.replyTo && !isSentView && (
                        <span
                          style={{
                            marginLeft: "8px",
                            color: WEB_COLORS.secondaryText,
                            fontSize: "12px",
                          }}
                        >
                          Reply
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: "12px",
                        color: WEB_COLORS.secondaryText,
                      }}
                    >
                      {formatTimestamp(message.createdAt)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: WEB_COLORS.secondaryText,
                      marginBottom: "6px",
                    }}
                  >
                    {isSentView ? `To: ${participant}` : `From: ${participant}`}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: WEB_COLORS.bodyText,
                      marginBottom: "8px",
                    }}
                  >
                    {message.body}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        borderRadius: "999px",
                        padding: "2px 10px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: WEB_COLORS.surface,
                        backgroundColor:
                          WEB_COLORS.priority[message.priority] ||
                          WEB_COLORS.secondaryText,
                      }}
                    >
                      {message.priority.toUpperCase()}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: WEB_COLORS.secondaryText,
                      }}
                    >
                      {message.type}
                    </span>
                    {message.isImportant && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: WEB_COLORS.priority.high,
                          fontWeight: 600,
                        }}
                      >
                        Important
                      </span>
                    )}
                    {isSentView && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: WEB_COLORS.accent,
                          fontWeight: 600,
                        }}
                      >
                        Sent
                      </span>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {!isSentView && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleReply(message);
                      }}
                      style={{
                        ...ACTION_BUTTON_STYLE,
                        border: `1px solid ${WEB_COLORS.border}`,
                        backgroundColor: WEB_COLORS.surface,
                        cursor: "pointer",
                      }}
                    >
                      Reply
                    </button>
                  )}
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      void toggleImportant(
                        message.id as string,
                        !message.isImportant,
                      );
                    }}
                    style={{
                      ...ACTION_BUTTON_STYLE,
                      border: `1px solid ${WEB_COLORS.border}`,
                      backgroundColor: message.isImportant
                        ? WEB_COLORS.importantLight
                        : WEB_COLORS.surface,
                      cursor: "pointer",
                    }}
                  >
                    {message.isImportant
                      ? "Unmark Important"
                      : "Mark Important"}
                  </button>
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      void deleteMessage(message.id as string);
                    }}
                    style={{
                      ...ACTION_BUTTON_STYLE,
                      border: `1px solid ${WEB_COLORS.danger}`,
                      backgroundColor: WEB_COLORS.dangerLight,
                      color: WEB_COLORS.danger,
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ComposeMessageModal
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
        sender={currentUser}
        replyTo={replyTo}
        onSent={() => setReplyTo(null)}
      />
    </div>
  );
}
